/**
 * All Sites ▸ Alert Board — the cross-site honeycomb, next to the Live Wall in the same scope.
 *
 * Every camera of every site on one canvas, a panel per site, one merged alert feed and one
 * docked alert panel. It is the same shared board the per-site page mounts; what differs is
 * only the api it is given — `createMultiSiteHexAlertsApi` fans the per-node tunnel calls out
 * across the estate — and `singleSite` being off, which is what makes the honeycomb draw its
 * site panels and offer the Site filter ("All sites" plus a chip per site).
 *
 * WHY THE PAGE, NOT AlertBoardPage: the per-site wrapper is bound to the selected node at
 * every turn — its api reads the stored scope, its modal fetches the alert from the selected
 * node, its toggle navigates to that node's map. In All Sites scope there IS no selected node,
 * so those all have to be answered per ALERT instead: the board's camera ids are
 * site-qualified (`nodeId::cameraId`), and this page reads the site back out of them.
 *
 * ALL THREE LAYOUTS, like the per-site board: the honeycomb, GIS View and Map View. Each
 * spatial board gets its own cross-site api — `createMultiSiteMapLayoutApi` lists every site's
 * floor plans in one picker ("HQ · Ground floor"), `createMultiSiteGeoMapApi` puts every site's
 * cameras on the one geographic map — and both emit the same site-qualified camera ids as the
 * alerts api, which is what lets the boards match pins to alerts. Editing is per site only
 * (`canEdit={false}`; the adapters reject writes): a plan, its pins and a map's default view
 * all belong to one node. The view lives in the URL — /all-sites/alert-board, …/gis, …/map.
 *
 * LAYOUT: the board's root is `flex h-full flex-col` with a `lg:flex-1` canvas inside it, so it
 * needs a parent with a DEFINITE height — Layout's <main> provides one, and `h-full` here
 * passes it down. A plain auto-height wrapper collapses the canvas to zero and the honeycomb
 * vanishes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  AlertBoardPage as SharedAlertBoardPage,
  Modal,
  RecordPager,
  type BoardView,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '@trinetra/shared';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import HexLivePlayer, { HexStreamProvider } from '../components/hexagon/HexLivePlayer';
import {
  createMultiSiteHexAlertsApi,
  splitSiteCameraKey,
  type SiteRef,
} from '../api/multiSiteHexAlerts.adapter';
import { createMultiSiteMapLayoutApi } from '../api/multiSiteMapLayouts.adapter';
import { createMultiSiteGeoMapApi } from '../api/multiSiteGeoMap.adapter';
import { AlertDetailView } from './AlertDetailPage';

const VIEW_PATH: Record<BoardView, string> = {
  hex: '/all-sites/alert-board',
  gis: '/all-sites/alert-board/gis',
  map: '/all-sites/alert-board/map',
};

export default function AllSitesAlertBoardPage() {
  const { nodes, selectNode } = useNode();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // WHICH sites the board covers, as a value that only changes when the roster does. The node
  // list is re-polled every 10s (status, metrics, session counts), and the board tears down and
  // rebuilds its entire subscription — N sockets, N snapshots — whenever its api changes, so
  // anything derived from `nodes` has to be keyed on the ids alone.
  const siteKey = useMemo(() => nodes.map((n) => n.nodeId).sort().join(','), [nodes]);
  const sites = useMemo<SiteRef[]>(
    () => nodes.map((n) => ({ nodeId: n.nodeId, name: n.name })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteKey],
  );

  // Offline sites are deliberately INCLUDED: their cameras simply fail to load and their
  // events socket retries with backoff, refetching that site's alerts the moment its tunnel
  // comes back. Filtering by status instead would rebuild every other site's subscription each
  // time any one site flapped.
  const alertsApi = useMemo(() => createMultiSiteHexAlertsApi(sites), [sites]);
  // Same roster key for the spatial boards, and the same offline policy: a site that doesn't
  // answer contributes no plans / pins rather than failing the board.
  const mapApi = useMemo(() => createMultiSiteMapLayoutApi(sites), [sites]);
  const geoApi = useMemo(() => createMultiSiteGeoMapApi(sites), [sites]);

  // Layouts by permission, exactly as the per-site board: `alerts.view` is the honeycomb,
  // `map.view` both spatial boards (one entitlement drawn on two backdrops).
  const allowedViews: BoardView[] = [
    ...(hasPermission('alerts.view') ? (['hex'] as BoardView[]) : []),
    ...(hasPermission('map.view') ? (['gis', 'map'] as BoardView[]) : []),
  ];
  const requested: BoardView =
    pathname === VIEW_PATH.map ? 'map' : pathname === VIEW_PATH.gis ? 'gis' : 'hex';
  const view = allowedViews.includes(requested) ? requested : allowedViews[0];

  // `replace`: flipping the layout is not a history entry — Back leaves the board.
  const goToView = useCallback(
    (v: BoardView) => navigate(VIEW_PATH[v], { replace: true }),
    [navigate],
  );

  // A deep link to a layout this user isn't entitled to: put the address bar back on the one
  // actually on screen.
  useEffect(() => {
    if (view && view !== requested) goToView(view);
  }, [view, requested, goToView]);

  // The alert being read, held as the panel's LIST plus a cursor rather than as one alert: an
  // operator working a board sweeps its alerts, and pages with the arrow keys instead of
  // closing and re-clicking.
  const [viewing, setViewing] = useState<{ list: ViewAlertTarget[]; index: number } | null>(null);
  const current = viewing ? viewing.list[viewing.index] : null;
  // Every target carries a site-qualified camera id, which is how a cross-site board knows
  // whose tunnel to fetch this record through.
  const currentSite = current ? splitSiteCameraKey(current.cameraId).nodeId : null;

  /** Leave the board for a page that only exists inside one site, taking the scope with us —
   *  otherwise the destination opens in All Sites and finds no node to ask. */
  const openInSite = useCallback(
    (nodeId: string | null, path: string) => {
      if (nodeId) selectNode(nodeId);
      navigate(path);
    },
    [navigate, selectNode],
  );

  return (
    <HexStreamProvider>
      <SharedAlertBoardPage
        alertsApi={alertsApi}
        mapApi={mapApi}
        geoApi={geoApi}
        PlayerComponent={HexLivePlayer}
        // Looking only: plans, pins and default views are edited on the site that owns them.
        canEdit={false}
        allowedViews={allowedViews}
        view={view}
        onViewChange={goToView}
        // The sidebar points at the honeycomb's URL, so the last layout used here is restored
        // on the way in — under its own key, apart from every site's per-node preference.
        rememberView
        persistKey="all-sites"
        // A panel per site, and the Site filter row that goes with it. This is the board the
        // shared page's multi-site path was written for.
        singleSite={false}
        // No node is selected in this scope and none is required — the board reloads when the
        // ROSTER changes instead.
        nodeKey={siteKey}
        onOpenRecordings={
          hasPermission('recordings.view')
            ? (cameraKey: string) => {
                const { nodeId, cameraId } = splitSiteCameraKey(cameraKey);
                openInSite(nodeId, `/recordings?camera=${cameraId}`);
              }
            : undefined
        }
        onViewAlert={({ id, cameraId, siblings, index }: ViewAlertRequest) => {
          // No alert id means the card carries no stored row yet (the node broadcasts before
          // its storage lane writes) — there is nothing to open, so fall back to that site's
          // alert list.
          if (!id) {
            const { nodeId, cameraId: camId } = splitSiteCameraKey(cameraId);
            openInSite(nodeId, `/alerts?camera_id=${camId}`);
            return;
          }
          // Keep the panel's list so the arrow keys can walk it. Alerts without a stored row
          // are dropped: stepping onto one would open a record that cannot be fetched.
          const list = (siblings || []).filter((s) => !!s.id);
          const at = list.findIndex((s) => s.id === id);
          setViewing(
            list.length
              ? { list, index: at >= 0 ? at : (index ?? 0) }
              : { list: [{ id, cameraId, name: cameraId }], index: 0 },
          );
        }}
      />

      <Modal
        open={!!current}
        onClose={() => setViewing(null)}
        title={current && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{current.alertName || 'Alert'}</span>
            <span className="text-[13px] font-normal text-muted-foreground">· Alert Details</span>
          </span>
        )}
        ariaLabel="Alert details"
        subtitle={current?.name}
        size="full"
        headerActions={current && viewing && (
          <>
            {/* Steps through the alerts the panel was showing when this one was opened —
                buttons and the arrow keys both. */}
            <RecordPager
              index={viewing.index}
              count={viewing.list.length}
              onIndexChange={(i) => setViewing({ list: viewing.list, index: i })}
              noun="alerts"
            />
            <button
              type="button"
              // The routed alert page reads the SELECTED site, so opening it from here has to
              // switch scope to the alert's own site first.
              onClick={() => openInSite(currentSite, `/alerts/${current.id}`)}
              title="Open the full alert page"
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
            </button>
          </>
        )}
      >
        {/* Keyed on the alert id so stepping to another card refetches instead of showing the
            previous alert's data while the new one loads. `nodeId` is the alert's own site —
            there is no selected one to fall back on here. */}
        {current?.id && (
          <AlertDetailView
            key={current.id}
            alertId={current.id}
            nodeId={currentSite || undefined}
            showTitle={false}
          />
        )}
      </Modal>
    </HexStreamProvider>
  );
}
